import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useMonthlyWeatherStatistics } from '../../api/weatherHistory';
import { formatNumber } from '../../utils/units';
import { FDI_THRESHOLDS as criticalThresholds } from '../../utils/fireDanger';

const logTicksFromValues = (values: number[], domainMin: number, domainMax: number) => {
  const ticks = values
    .map((v) => Math.log10(Math.max(v, 1)))
    .filter((t) => Number.isFinite(t) && t >= domainMin && t <= domainMax);

  // Sort + unique
  return Array.from(new Set(ticks)).sort((a, b) => a - b);
};

const CriticalTooltip: React.FC<{ active?: boolean; label?: string; payload?: any[] }> = ({ active, label, payload }) => {
  if (!active || !payload || payload.length === 0) return null;

  const p = payload[0]?.payload;
  if (!p) return null;

  const fromLog = (logValue: any) => Math.pow(10, Number(logValue));
  const renderValue = (logValue: any) => formatNumber(fromLog(logValue), { maximumFractionDigits: 0 });

  return (
    <Box
      sx={{
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>{label}</Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 2, rowGap: 0.5, mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Min</Typography>
        <Typography variant="caption" sx={{ color: '#fff' }}>{renderValue(p.fire_danger_min)}</Typography>

        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>1st Quartile</Typography>
        <Typography variant="caption" sx={{ color: '#fff' }}>{renderValue(p.fire_danger_q1)}</Typography>

        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Median</Typography>
        <Typography variant="caption" sx={{ color: '#fff' }}>{renderValue(p.fire_danger_median)}</Typography>

        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>3rd Quartile</Typography>
        <Typography variant="caption" sx={{ color: '#fff' }}>{renderValue(p.fire_danger_q3)}</Typography>

        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Max</Typography>
        <Typography variant="caption" sx={{ color: '#fff' }}>{renderValue(p.fire_danger_max)}</Typography>
      </Box>
    </Box>
  );
};

interface FireDangerIndexViewProps {
  projectId: string;
}

// Custom box plot rendering component
const BoxPlot = (props: any) => {
  const { x, y, width, height, payload, domainMin, domainMax, background } = props;
  
  if (!payload || payload.fire_danger_min === null || payload.fire_danger_min === undefined) return null;
  
  const { fire_danger_min, fire_danger_q1, fire_danger_median, fire_danger_q3, fire_danger_max } = payload;
  
  // Calculate positions (Y-axis is inverted in SVG)
  const plotY = background?.y ?? y;
  const plotHeight = background?.height ?? height;
  const yScale = plotHeight / (domainMax - domainMin);
  const baseY = plotY + plotHeight;

  const minY = baseY - ((fire_danger_min - domainMin) * yScale);
  const q1Y = baseY - ((fire_danger_q1 - domainMin) * yScale);
  const medianY = baseY - ((fire_danger_median - domainMin) * yScale);
  const q3Y = baseY - ((fire_danger_q3 - domainMin) * yScale);
  const maxY = baseY - ((fire_danger_max - domainMin) * yScale);
  
  const boxWidth = width * 0.6;
  const centerX = x + width / 2;
  
  return (
    <g>
      {/* Whisker lines */}
      <line x1={centerX} y1={minY} x2={centerX} y2={q1Y} stroke="#ff9800" strokeWidth={2} />
      <line x1={centerX} y1={q3Y} x2={centerX} y2={maxY} stroke="#ff9800" strokeWidth={2} />
      
      {/* Min/Max caps */}
      <line x1={centerX - boxWidth/4} y1={minY} x2={centerX + boxWidth/4} y2={minY} stroke="#ff9800" strokeWidth={2} />
      <line x1={centerX - boxWidth/4} y1={maxY} x2={centerX + boxWidth/4} y2={maxY} stroke="#ff9800" strokeWidth={2} />
      
      {/* Box (Q1 to Q3) */}
      <rect
        x={centerX - boxWidth/2}
        y={q3Y}
        width={boxWidth}
        height={q1Y - q3Y}
        fill="rgba(255, 152, 0, 0.3)"
        stroke="#ff9800"
        strokeWidth={2}
      />
      
      {/* Median line */}
      <line
        x1={centerX - boxWidth/2}
        y1={medianY}
        x2={centerX + boxWidth/2}
        y2={medianY}
        stroke="#ff9800"
        strokeWidth={3}
      />
    </g>
  );
};

const FireDangerIndexView: React.FC<FireDangerIndexViewProps> = ({ projectId }) => {
  const { data: monthlyStats, isLoading } = useMonthlyWeatherStatistics(projectId);

  const MIN_FDI = 1;
  const toLogFdi = (value: number | null | undefined) => {
    const safe = Math.max(value ?? 0, MIN_FDI);
    return Math.log10(safe);
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const chartData = monthlyStats.map((stat) => ({
    month: monthNames[stat.month - 1],
    fire_danger_min: toLogFdi(stat.fire_danger_min),
    fire_danger_q1: toLogFdi(stat.fire_danger_q1),
    fire_danger_median: toLogFdi(stat.fire_danger_median),
    fire_danger_q3: toLogFdi(stat.fire_danger_q3),
    fire_danger_max: toLogFdi(stat.fire_danger_max),
  }));

  const maxObservedLog = chartData.reduce((max, d) => (d.fire_danger_max > max ? d.fire_danger_max : max), toLogFdi(100));
  const domainMin = toLogFdi(MIN_FDI);
  const domainMax = Math.max(maxObservedLog, toLogFdi(100));

  const yTicks = logTicksFromValues(
    [1, 2, 4, 8, 16, 32, 64, 128, 256],
    domainMin,
    domainMax
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress sx={{ color: '#ff6b35' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, p: 3 }}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
          Fire Danger Index (FDI) Seasonality
        </Typography>

        <ResponsiveContainer width="100%" height={500}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
            <XAxis
              dataKey="month"
              stroke="rgba(255, 255, 255, 0.5)"
              tick={{ fill: 'rgba(255, 255, 255, 0.7)' }}
            />
            <YAxis
              stroke="rgba(255, 255, 255, 0.5)"
              tick={{ fill: 'rgba(255, 255, 255, 0.7)' }}
              domain={[domainMin, domainMax]}
              ticks={yTicks}
              tickFormatter={(value: any) => {
                const original = Math.pow(10, Number(value));
                return formatNumber(original, { maximumFractionDigits: 0 });
              }}
            />
            <Tooltip content={<CriticalTooltip />} />
            
            {/* Danger level reference lines - AFDRS classifications */}
            {criticalThresholds.map((t) => (
              <ReferenceLine
                key={t.label}
                y={toLogFdi(t.value)}
                stroke={t.color}
                strokeDasharray="3 3"
                label={{ value: t.label, fill: t.color, position: 'right' }}
              />
            ))}

            {/* Invisible bar to provide structure for custom box plot */}
            <Bar dataKey="fire_danger_median" shape={<BoxPlot domainMin={domainMin} domainMax={domainMax} />} />
          </ComposedChart>
        </ResponsiveContainer>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block', mb: 1 }}>
            Critical thresholds
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {criticalThresholds.map((t) => (
              <Box key={t.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, bgcolor: t.color, borderRadius: '50%' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {t.label}: {formatNumber(t.value, { maximumFractionDigits: 0 })}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

      </Box>
    </Box>
  );
};

export default FireDangerIndexView;
